import { useState, useEffect } from "react";
import API from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { UK_REGIONS, UK_COUNTY_GROUPS, ALL_UK_AREAS } from "../lib/ukAreas";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const UkAreasPicker = ({ value = [], onChange, testIdPrefix = "areas", allowAdd = false, regions, countyGroups }) => {
  const REGIONS = regions && regions.length ? regions : UK_REGIONS;
  const GROUPS = countyGroups && Object.keys(countyGroups).length ? countyGroups : UK_COUNTY_GROUPS;
  const ALL_AREAS = regions || countyGroups ? [...REGIONS, ...Object.values(GROUPS).flat()] : ALL_UK_AREAS;
  const [filter, setFilter] = useState("");
  const [customAreas, setCustomAreas] = useState([]);
  const [newArea, setNewArea] = useState("");
  const [addingArea, setAddingArea] = useState(false);
  const selected = new Set(value);
  const toggle = (area) => {
    const next = new Set(selected);
    if (next.has(area)) next.delete(area); else next.add(area);
    onChange([...next]);
  };
  const match = (a) => a.toLowerCase().includes(filter.trim().toLowerCase());

  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    API.get("/suppliers/area-options")
      .then(({ data }) => setCustomAreas(data.areas || []))
      .catch(() => {});
  }, [user]);

  const addCustomArea = async () => {
    const name = newArea.trim().replace(/\s+/g, " ");
    if (!name) return;
    const std = ALL_AREAS.find(a => a.toLowerCase() === name.toLowerCase());
    if (std) {
      if (!selected.has(std)) toggle(std);
      toast.info(`"${std}" is already in the standard list — selected it`);
      setNewArea("");
      return;
    }
    const existing = customAreas.find(a => a.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!selected.has(existing.name)) toggle(existing.name);
      setNewArea("");
      return;
    }
    setAddingArea(true);
    try {
      const { data } = await API.post("/suppliers/area-options", { name });
      setCustomAreas(prev => [...prev, { id: data.id, name: data.name }].sort((a, b) => a.name.localeCompare(b.name)));
      if (!selected.has(data.name)) toggle(data.name);
      toast.success(`"${data.name}" added — now available for all suppliers`);
      setNewArea("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add area"); }
    finally { setAddingArea(false); }
  };

  const removeCustomArea = async (area) => {
    try {
      await API.delete(`/suppliers/area-options/${area.id}`);
      setCustomAreas(prev => prev.filter(a => a.id !== area.id));
      if (selected.has(area.name)) toggle(area.name);
      toast.success(`"${area.name}" removed from the list`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove area"); }
  };

  const visibleCustom = customAreas.filter(a => match(a.name));

  return (
    <div className="border border-border rounded-md" data-testid={`${testIdPrefix}-picker`}>
      <div className="p-2 border-b border-border space-y-2">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Type to find a county..." className="h-8 text-xs" data-testid={`${testIdPrefix}-picker-filter`} />
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {REGIONS.filter(match).map((r) => (
            <label key={r} className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer">
              <Checkbox checked={selected.has(r)} onCheckedChange={() => toggle(r)} data-testid={`${testIdPrefix}-region-${slug(r)}`} />
              {r}
            </label>
          ))}
        </div>
      </div>
      <div className="max-h-44 overflow-y-auto p-2 space-y-2">
        {Object.entries(GROUPS).map(([country, counties]) => {
          const visible = counties.filter(match);
          if (!visible.length) return null;
          return (
            <div key={country}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{country}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1">
                {visible.map((c) => (
                  <label key={c} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={selected.has(c)} onCheckedChange={() => toggle(c)} data-testid={`${testIdPrefix}-county-${slug(c)}`} />
                    <span className="truncate">{c}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
        {visibleCustom.length > 0 && (
          <div data-testid={`${testIdPrefix}-custom-group`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Custom Areas</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1">
              {visibleCustom.map((a) => (
                <label key={a.id} className="flex items-center gap-1.5 text-xs cursor-pointer group">
                  <Checkbox checked={selected.has(a.name)} onCheckedChange={() => toggle(a.name)} data-testid={`${testIdPrefix}-custom-${slug(a.name)}`} />
                  <span className="truncate">{a.name}</span>
                  {allowAdd && (
                    <button type="button" onClick={(e) => { e.preventDefault(); removeCustomArea(a); }} title="Remove from list" className="opacity-0 group-hover:opacity-100 text-destructive transition-opacity" data-testid={`${testIdPrefix}-custom-remove-${slug(a.name)}`}>
                      <X size={10} />
                    </button>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      {allowAdd && (
        <div className="px-2 py-1.5 border-t border-border flex items-center gap-1.5" data-testid={`${testIdPrefix}-add-custom-row`}>
          <Input
            value={newArea}
            onChange={(e) => setNewArea(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomArea(); } }}
            placeholder="Add a specific area (e.g. Cardiff Bay, NP10)..."
            className="h-7 text-xs flex-1"
            maxLength={60}
            data-testid={`${testIdPrefix}-add-custom-input`}
          />
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs px-2" onClick={addCustomArea} disabled={addingArea || !newArea.trim()} data-testid={`${testIdPrefix}-add-custom-btn`}>
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
      )}
      {value.length > 0 && (
        <div className="px-2 py-1.5 border-t border-border flex flex-wrap gap-1 items-center" data-testid={`${testIdPrefix}-picker-selected`}>
          <span className="text-[10px] text-muted-foreground mr-1">{value.length} selected — click to remove:</span>
          {value.map((a) => (
            <Badge key={a} variant="secondary" className="text-[9px] cursor-pointer" onClick={() => toggle(a)} data-testid={`${testIdPrefix}-selected-${slug(a)}`}>
              {a} ✕
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
